import { Controller } from "@hotwired/stimulus"

const seatColor = name =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim()

const STATUS_COLORS = () => ({
  available: seatColor("--seat-available"),
  reserved:  seatColor("--seat-reserved"),
  sold:      seatColor("--seat-sold"),
  locked:    seatColor("--seat-locked"),
  selected:  seatColor("--seat-selected")
})

export default class extends Controller {
  static targets = [
    "seat",
    "panel",
    "error",
    "seatDataJson",
    "eventPricesJson",
    "availableTemplate",
    "lockedTemplate",
    "soldTemplate",
    "reservedTemplate",
    "priceEditorTemplate",
    "sellButton",
    "posPanel",
    "posForm",
    "posEmpty",
    "posItemList",
    "posItemTemplate",
    "posTotal",
    "posEmail",
    "cashButton",
    "qrButton",
    "posSuccess",
    "posTicketList",
    "posEmailedNote",
    "posOrderPdfLink",
    "priceDialog",
    "priceDialogSeat",
    "priceDialogOptions",
    "priceDialogEmpty",
    "qrDialog",
    "qrImage"
  ]

  static values = {
    showId: Number,
    eventId: Number,
    rowLabel: { type: String, default: "Row" },
    numberLabel: { type: String, default: "Seat" }
  }

  connect() {
    const raw = this.seatDataJsonTarget.textContent
    const list = JSON.parse(raw)
    this.seatsById = new Map(list.map(s => [String(s.id), s]))
    this.eventPrices = this.hasEventPricesJsonTarget
      ? JSON.parse(this.eventPricesJsonTarget.textContent)
      : []
    this.selectedSeat = null
    this.statusColors = STATUS_COLORS()

    this.sellMode = false
    this.qrPollTimer = null
    this.qrSessionId = null

    // Re-hydrate the staff's own pending POS selection after a page reload.
    this.posItems = new Map()
    this.seatsById.forEach(seat => {
      if (seat.reservation?.mine && seat.reservation.basket_item_id) {
        this.posItems.set(String(seat.id), {
          itemId: seat.reservation.basket_item_id,
          price: seat.reservation.price
        })
      }
    })

    this.seatTargets.forEach(el => this.paintSeat(el))
    this.renderPosItems()

    // A pending POS sale (e.g. after a page reload) goes straight back
    // into sell mode so the staff can continue the checkout.
    if (this.posItems.size > 0) this.setSellMode(true)
  }

  disconnect() {
    this.stopQrPolling()
  }

  paintSeat(el) {
    const status = this.posItems.has(String(el.dataset.seatId)) ? "selected" : el.dataset.seatStatus
    el.setAttribute("fill", this.statusColors[status] || this.statusColors.available)
  }

  selectSeat(event) {
    const el = event.currentTarget
    const seatId = el.dataset.seatId

    if (this.sellMode && this.handlePosSeatClick(seatId)) return

    this.seatTargets.forEach(s => {
      s.setAttribute("stroke", seatColor("--seat-stroke"))
      s.setAttribute("stroke-width", "1")
    })
    el.setAttribute("stroke", seatColor("--seat-stroke-selected"))
    el.setAttribute("stroke-width", "3")
    this.selectedSeat = el

    this.renderPanel(seatId)
    this.clearError()
  }

  renderPanel(seatId) {
    const data = this.seatsById.get(String(seatId))
    if (!data) return

    const template = this.templateForStatus(data.status)
    if (!template) return

    const fragment = template.content.cloneNode(true)
    this.populateFragment(fragment, data)

    this.panelTarget.innerHTML = ""
    this.panelTarget.appendChild(fragment)
  }

  templateForStatus(status) {
    switch (status) {
      case "available": return this.availableTemplateTarget
      case "locked":    return this.lockedTemplateTarget
      case "sold":      return this.soldTemplateTarget
      case "reserved":  return this.reservedTemplateTarget
      default:          return null
    }
  }

  populateFragment(fragment, data) {
    const seatLabel = fragment.querySelector('[data-field="seatLabel"]')
    if (seatLabel) seatLabel.textContent = this.formatSeatLabel(data)

    const priceSection = fragment.querySelector('[data-field="priceSection"]')
    if (priceSection) this.renderPriceEditor(priceSection, data)

    if (data.ticket) {
      const t = data.ticket
      const link = fragment.querySelector('[data-field="purchaserLink"]')
      if (link) {
        link.href = `mailto:${t.purchaser_email}`
        link.textContent = t.purchaser_email
      }
      const orderId = fragment.querySelector('[data-field="orderId"]')
      if (orderId) orderId.textContent = t.token

      const price = fragment.querySelector('[data-field="price"]')
      if (price) price.textContent = t.price

      const purchasedAt = fragment.querySelector('[data-field="purchasedAt"]')
      if (purchasedAt) purchasedAt.textContent = t.purchased_at

      const ticketLink = fragment.querySelector('[data-field="ticketLink"]')
      if (ticketLink) ticketLink.href = t.public_url
    }

    if (data.reservation) {
      const reservedUntil = fragment.querySelector('[data-field="reservedUntil"]')
      if (reservedUntil) reservedUntil.textContent = data.reservation.reserved_until

      const priceName = fragment.querySelector('[data-field="priceName"]')
      if (priceName) priceName.textContent = data.reservation.price_name || "—"
    }
  }

  formatSeatLabel(data) {
    const parts = []
    if (data.block) parts.push(data.block)
    if (data.row) parts.push(`${this.rowLabelValue} ${data.row}`)
    if (data.number) parts.push(`${this.numberLabelValue} ${data.number}`)
    return parts.join(" · ")
  }

  renderPriceEditor(container, data) {
    if (!this.hasPriceEditorTemplateTarget) return

    const fragment = this.priceEditorTemplateTarget.content.cloneNode(true)
    const list = fragment.querySelector('[data-field="priceList"]')
    const noPricesMessage = fragment.querySelector('[data-field="noPricesMessage"]')

    if (this.eventPrices.length === 0) {
      if (list) list.remove()
      const saveButton = fragment.querySelector('button')
      if (saveButton) saveButton.remove()
      if (noPricesMessage) noPricesMessage.classList.remove("hidden")
    } else {
      const seatPriceIds = new Set((data.price_ids || []).map(String))
      this.eventPrices.forEach(price => {
        const wrapper = document.createElement("div")
        wrapper.className = "flex items-center gap-2"

        const input = document.createElement("input")
        input.type = "checkbox"
        input.className = "size-4 rounded border-slate-300 text-primary focus:ring-primary/30"
        input.id = `seat-${data.id}-price-${price.id}`
        input.value = String(price.id)
        input.dataset.role = "price-checkbox"
        if (seatPriceIds.has(String(price.id))) input.checked = true

        const label = document.createElement("label")
        label.className = "text-xs text-slate-700"
        label.htmlFor = input.id
        label.textContent = `${price.name} — ${price.formatted_price}`

        wrapper.appendChild(input)
        wrapper.appendChild(label)
        list.appendChild(wrapper)
      })
    }

    container.appendChild(fragment)
  }

  async savePrices(event) {
    if (!this.selectedSeat) return
    const seatId = this.selectedSeat.dataset.seatId
    const root = event.currentTarget.closest('[data-field="priceSection"]') || this.panelTarget
    const checkboxes = root.querySelectorAll('input[data-role="price-checkbox"]:checked')
    const priceIds = Array.from(checkboxes).map(cb => cb.value)
    const url = `/backstage/shows/${this.showIdValue}/events/${this.eventIdValue}/event_seats/${seatId}`

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": Rails.csrfToken()
        },
        body: JSON.stringify({ event_seat: { price_ids: priceIds } })
      })

      const data = await response.json()

      if (data.success) {
        const local = this.seatsById.get(String(seatId))
        if (local) local.price_ids = data.price_ids
        const savedMessage = this.panelTarget.querySelector('[data-field="pricesSavedMessage"]')
        if (savedMessage) {
          savedMessage.classList.remove("hidden")
          setTimeout(() => savedMessage.classList.add("hidden"), 2000)
        }
        this.clearError()
      } else {
        this.showError(data.error)
      }
    } catch (err) {
      console.error(err)
      this.showError(err.message)
    }
  }

  lockSeat() {
    this.changeStatus("locked")
  }

  unlockSeat() {
    this.changeStatus("available")
  }

  async changeStatus(targetStatus) {
    if (!this.selectedSeat) return
    const seatId = this.selectedSeat.dataset.seatId
    const url = `/backstage/shows/${this.showIdValue}/events/${this.eventIdValue}/event_seats/${seatId}`

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": Rails.csrfToken()
        },
        body: JSON.stringify({ event_seat: { status: targetStatus } })
      })

      const data = await response.json()

      if (data.success) {
        this.applyStatusChange(seatId, data.status)
      } else {
        this.showError(data.error)
      }
    } catch (err) {
      console.error(err)
      this.showError(err.message)
    }
  }

  applyStatusChange(seatId, newStatus) {
    const local = this.seatsById.get(String(seatId))
    if (local) {
      local.status = newStatus
      if (newStatus !== "sold") local.ticket = null
      if (newStatus !== "reserved") local.reservation = null
    }
    if (this.selectedSeat) {
      this.selectedSeat.dataset.seatStatus = newStatus
      this.paintSeat(this.selectedSeat)
    }
    this.renderPanel(seatId)
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  clearError() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }

  // --- POS sale (sell mode) ---

  toggleSellMode() {
    this.setSellMode(!this.sellMode)
  }

  setSellMode(enabled) {
    this.sellMode = enabled

    const button = this.sellButtonTarget
    button.textContent = enabled ? button.dataset.labelOn : button.dataset.labelOff

    this.posPanelTarget.classList.toggle("hidden", !enabled)
    this.clearError()
  }

  // Returns true when the click was consumed by the POS flow.
  handlePosSeatClick(seatId) {
    const data = this.seatsById.get(String(seatId))
    if (!data) return false

    if (this.posItems.has(String(seatId))) {
      this.removePosItem(seatId)
      return true
    }

    if (data.status === "available") {
      this.openPriceDialog(data)
      return true
    }

    return false
  }

  openPriceDialog(data) {
    this.priceDialogSeatTarget.textContent = this.formatSeatLabel(data)

    const options = this.priceDialogOptionsTarget
    options.innerHTML = ""

    const prices = this.eventPrices.filter(price =>
      (data.price_ids || []).map(String).includes(String(price.id))
    )

    this.priceDialogEmptyTarget.classList.toggle("hidden", prices.length > 0)
    options.classList.toggle("hidden", prices.length === 0)

    prices.forEach(price => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition hover:bg-surface focus:bg-surface focus:outline-none"

      const name = document.createElement("span")
      name.className = "text-sm font-medium text-slate-900"
      name.textContent = price.name

      const amount = document.createElement("span")
      amount.className = "text-sm font-semibold text-ink"
      amount.textContent = price.formatted_price

      button.appendChild(name)
      button.appendChild(amount)
      button.addEventListener("click", () => {
        this.priceDialogTarget.close()
        this.addPosItem(data.id, price.id)
      })
      options.appendChild(button)
    })

    this.priceDialogTarget.showModal()
  }

  async addPosItem(seatId, priceId) {
    const data = await this.posRequest("POST", "/sale_items", {
      event_seat_id: seatId,
      price_id: priceId
    })
    if (!data) return

    this.posItems.set(String(seatId), { itemId: data.item_id, price: data.price })

    const seat = this.seatsById.get(String(seatId))
    if (seat) {
      seat.status = "reserved"
      seat.reservation = { mine: true, basket_item_id: data.item_id, price: data.price }
    }

    this.repaintSeat(seatId, "reserved")
    this.renderPosItems()
  }

  async removePosItem(seatId) {
    const item = this.posItems.get(String(seatId))
    if (!item) return

    const data = await this.posRequest("DELETE", `/sale_items/${item.itemId}`)
    if (!data) return

    this.posItems.delete(String(seatId))

    const seat = this.seatsById.get(String(seatId))
    if (seat) {
      seat.status = "available"
      seat.reservation = null
    }

    this.repaintSeat(seatId, "available")
    this.renderPosItems()
  }

  renderPosItems() {
    if (!this.hasPosItemListTarget) return

    const list = this.posItemListTarget
    list.innerHTML = ""

    let totalCents = 0
    let currency = null

    this.posItems.forEach((item, seatId) => {
      const seat = this.seatsById.get(String(seatId))
      const fragment = this.posItemTemplateTarget.content.cloneNode(true)

      fragment.querySelector('[data-field="seatLabel"]').textContent = seat ? this.formatSeatLabel(seat) : ""
      fragment.querySelector('[data-field="priceLabel"]').textContent =
        item.price ? `${item.price.name} — ${item.price.formatted_price}` : ""
      fragment.querySelector('[data-field="removeButton"]').addEventListener("click", () => this.removePosItem(seatId))

      list.appendChild(fragment)

      if (item.price) {
        totalCents += item.price.cents
        currency = currency || item.price.currency
      }
    })

    this.posTotalTarget.textContent = currency
      ? `${currency.toUpperCase()} ${(totalCents / 100).toFixed(2)}`
      : "—"

    const empty = this.posItems.size === 0
    this.posEmptyTarget.classList.toggle("hidden", !empty)
    this.cashButtonTarget.disabled = empty
    this.qrButtonTarget.disabled = empty
  }

  async payCash() {
    this.setPayButtonsEnabled(false)

    const data = await this.posRequest("POST", "/cash_sale", { email: this.posEmailTarget.value })

    if (data) {
      this.completeSale(data)
    } else {
      this.setPayButtonsEnabled(true)
    }
  }

  async payQr() {
    this.setPayButtonsEnabled(false)

    const data = await this.posRequest("POST", "/qr_payment", { email: this.posEmailTarget.value })

    if (!data) {
      this.setPayButtonsEnabled(true)
      return
    }

    this.qrSessionId = data.session_id
    this.qrImageTarget.src = data.qr_data_uri
    this.qrDialogTarget.showModal()

    this.qrPollTimer = setInterval(() => this.pollQrPayment(), 3000)
  }

  async pollQrPayment() {
    const data = await this.posRequest("GET", `/qr_payment?session_id=${encodeURIComponent(this.qrSessionId)}`)
    if (!data) return

    if (data.status === "paid") {
      this.stopQrPolling()
      this.qrDialogTarget.close()
      this.completeSale(data)
    }
  }

  cancelQr() {
    this.stopQrPolling()
    this.qrDialogTarget.close()
    this.setPayButtonsEnabled(true)
  }

  stopQrPolling() {
    if (this.qrPollTimer) {
      clearInterval(this.qrPollTimer)
      this.qrPollTimer = null
    }
    this.qrSessionId = null
  }

  completeSale(data) {
    this.posItems.clear()

    data.tickets.forEach(ticket => {
      const seat = this.seatsById.get(String(ticket.seat_id))
      if (seat) {
        seat.status = "sold"
        seat.reservation = null
        seat.ticket = {
          token: ticket.token,
          purchaser_email: ticket.purchaser_email,
          stripe_session_id: ticket.stripe_session_id,
          price: ticket.price,
          purchased_at: ticket.purchased_at,
          public_url: ticket.public_url
        }
      }
      this.repaintSeat(ticket.seat_id, "sold")
    })

    this.posTicketListTarget.innerHTML = ""
    data.tickets.forEach(ticket => {
      const seat = this.seatsById.get(String(ticket.seat_id))
      const li = document.createElement("li")
      const link = document.createElement("a")
      link.href = ticket.public_url
      link.target = "_blank"
      link.rel = "noopener"
      link.className = "text-primary hover:text-primary-dark"
      link.textContent = seat ? this.formatSeatLabel(seat) : ticket.token
      li.appendChild(link)
      this.posTicketListTarget.appendChild(li)
    })

    const emailed = data.tickets.some(ticket => ticket.purchaser_email)
    this.posEmailedNoteTarget.classList.toggle("hidden", !emailed)
    this.posOrderPdfLinkTarget.href = data.order_pdf_url

    this.posFormTarget.classList.add("hidden")
    this.posSuccessTarget.classList.remove("hidden")
  }

  newSale() {
    this.posEmailTarget.value = ""
    this.posSuccessTarget.classList.add("hidden")
    this.posFormTarget.classList.remove("hidden")
    this.setPayButtonsEnabled(true)
    this.renderPosItems()
  }

  setPayButtonsEnabled(enabled) {
    const empty = this.posItems.size === 0
    this.cashButtonTarget.disabled = !enabled || empty
    this.qrButtonTarget.disabled = !enabled || empty
  }

  repaintSeat(seatId, status) {
    const el = this.seatTargets.find(s => s.dataset.seatId === String(seatId))
    if (!el) return

    el.dataset.seatStatus = status
    this.paintSeat(el)
  }

  async posRequest(method, path, body = null) {
    const base = `/backstage/shows/${this.showIdValue}/events/${this.eventIdValue}/pos`

    try {
      const response = await fetch(base + path, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": Rails.csrfToken()
        },
        body: body ? JSON.stringify(body) : null
      })

      const data = await response.json()

      if (!response.ok || data.success === false) {
        this.showError(data.error || response.statusText)
        return null
      }

      this.clearError()
      return data
    } catch (err) {
      console.error(err)
      this.showError(err.message)
      return null
    }
  }
}
